import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Send, Sparkles, Loader2, CheckCircle2, Wrench, Lightbulb, FileText, Tags, ListChecks, ClipboardCheck } from 'lucide-react';
import { useAuthInfo } from '@propelauth/react';
import { cn } from '../../../../../lib/utils';
import MarkdownRenderer from '../../../../../components/agno/MarkdownRenderer';
import useSkillBuilderStream, {
  type SkillBuilderDraftConfig,
  type SkillBuilderMessage,
} from '../hooks/useSkillBuilderStream';
import { ChatMessageAvatar, ChatSenderLabel, getChatUserInitials } from '../../../../../shared/chat/ChatMessageIdentity';

interface SkillChatBuilderProps {
  onBack?: () => void;
  showBackButton?: boolean;
  title?: string;
  subtitle?: string;
  externalPrompt?: string | null;
  onExternalPromptConsumed?: () => void;
  draftSkillConfig?: SkillBuilderDraftConfig | null;
  onDraftSkillConfigChange?: (draft: SkillBuilderDraftConfig | null) => void;
}

// Map ui_step to icons and loading labels
const TOOL_LOADING_LABELS: Record<string, string> = {
  show_skill_name_suggestions: 'Generating name suggestions...',
  generate_skill_instructions: 'Generating instructions...',
  refine_skill_instructions: 'Refining instructions...',
  suggest_keywords: 'Suggesting keywords...',
  suggest_structured_output: 'Suggesting output fields...',
  validate_skill_config: 'Validating configuration...',
  show_skill_summary: 'Preparing summary...',
  create_skill_from_config: 'Creating skill...',
};

const STEP_ICONS: Record<string, React.ReactNode> = {
  name_suggestions: <Lightbulb className="h-4 w-4" />,
  instructions_generated: <FileText className="h-4 w-4" />,
  instructions_refined: <FileText className="h-4 w-4" />,
  keyword_suggestions: <Tags className="h-4 w-4" />,
  structured_output_suggestions: <ListChecks className="h-4 w-4" />,
  validation_result: <ClipboardCheck className="h-4 w-4" />,
  skill_summary: <ClipboardCheck className="h-4 w-4" />,
  skill_created: <CheckCircle2 className="h-4 w-4 text-green-500" />,
};

const NameSuggestionsStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => (
  <div className="w-full max-w-md space-y-2">
    <p className="text-xs font-medium text-muted-foreground">Suggested names:</p>
    <div className="space-y-1.5">
      {(result.suggestions || []).map((name: string, i: number) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(name, name)}
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted"
        >
          {name}
        </button>
      ))}
    </div>
  </div>
);

const InstructionsGeneratedStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => {
  const markdown = result.instructions_markdown || '';

  return (
    <div className="w-full max-w-md space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Instructions generated for <span className="text-foreground">{result.skill_name}</span>
      </p>
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground line-clamp-4">
          {markdown.slice(0, 200)}{markdown.length > 200 ? '...' : ''}
        </p>
      </div>
      <p className="text-[10px] text-muted-foreground">
        View and edit full instructions in the right sidebar.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSelect('use_instructions', 'Use these instructions')}
          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          Use These Instructions
        </button>
        <button
          type="button"
          onClick={() => onSelect('refine_instructions', 'I want to refine them')}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          Refine
        </button>
      </div>
    </div>
  );
};

const InstructionsRefinedStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => {
  if (!result.success) {
    return (
      <div className="w-full max-w-md space-y-2">
        <p className="text-xs text-destructive">{result.error || 'Failed to refine instructions'}</p>
      </div>
    );
  }

  const markdown = result.instructions_markdown || '';

  return (
    <div className="w-full max-w-md space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Instructions refined</p>
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground line-clamp-4">
          {markdown.slice(0, 200)}{markdown.length > 200 ? '...' : ''}
        </p>
      </div>
      <p className="text-[10px] text-muted-foreground">
        View and edit updated instructions in the right sidebar.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSelect('use_instructions', 'Use these instructions')}
          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          Use These Instructions
        </button>
        <button
          type="button"
          onClick={() => onSelect('refine_instructions', 'Refine further')}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          Refine More
        </button>
      </div>
    </div>
  );
};

const KeywordSuggestionsStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => {
  const allKeywords: string[] = result.suggested_keywords || [];
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allKeywords));

  const toggleKeyword = (kw: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) {
        next.delete(kw);
      } else {
        next.add(kw);
      }
      return next;
    });
  };

  const allSelected = selected.size === allKeywords.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allKeywords));
  };

  const handleConfirm = () => {
    const chosen = allKeywords.filter((kw) => selected.has(kw));
    onSelect(JSON.stringify(chosen), chosen.join(', '));
  };

  return (
    <div className="w-full max-w-md space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">Suggested keywords:</p>
        <button
          type="button"
          onClick={toggleAll}
          className="text-[11px] font-medium text-primary hover:underline"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allKeywords.map((kw: string, i: number) => {
          const isSelected = selected.has(kw);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggleKeyword(kw)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                isSelected
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border bg-muted/30 text-muted-foreground line-through',
              )}
            >
              {kw}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          {selected.size === 0 ? 'Skip Keywords' : selected.size === allKeywords.length ? 'Use All Keywords' : `Use ${selected.size} Keyword${selected.size > 1 ? 's' : ''}`}
        </button>
        <button
          type="button"
          onClick={() => onSelect('custom', 'I\'ll set my own')}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          Customize
        </button>
      </div>
    </div>
  );
};

const StructuredOutputStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => {
  const allFields: any[] = result.suggested_fields || [];
  const [selected, setSelected] = useState<Set<number>>(() => new Set(allFields.map((_: any, i: number) => i)));

  const toggleField = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const allSelected = selected.size === allFields.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allFields.map((_: any, i: number) => i)));
  };

  const handleConfirm = () => {
    const chosen = allFields.filter((_: any, i: number) => selected.has(i));
    if (chosen.length === 0) {
      onSelect('skip', 'No structured output needed');
    } else {
      const names = chosen.map((f: any) => f.fieldName);
      onSelect(JSON.stringify(chosen), names.join(', '));
    }
  };

  return (
    <div className="w-full max-w-md space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">Suggested output fields:</p>
        <button
          type="button"
          onClick={toggleAll}
          className="text-[11px] font-medium text-primary hover:underline"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div className="space-y-1">
        {allFields.map((field: any, i: number) => {
          const isSelected = selected.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggleField(i)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md border px-3 py-1.5 text-left transition-colors',
                isSelected
                  ? 'border-primary/30 bg-primary/5'
                  : 'border-border bg-muted/10 opacity-50',
              )}
            >
              <span className={cn('text-xs font-medium', isSelected ? 'text-foreground' : 'text-muted-foreground line-through')}>
                {field.fieldName}
              </span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{field.fieldType}</span>
              {field.required && <span className="text-[10px] text-primary">required</span>}
              {field.description && (
                <span className="ml-auto text-[10px] text-muted-foreground">{field.description}</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground">
        You can add or edit fields in the right sidebar.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          {selected.size === 0 ? 'Skip Output Format' : selected.size === allFields.length ? 'Use All Fields' : `Use ${selected.size} Field${selected.size > 1 ? 's' : ''}`}
        </button>
        <button
          type="button"
          onClick={() => onSelect('skip', 'No structured output needed')}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

const SkillSummaryStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result, onSelect }) => {
  const config = result.skill_config || {};
  return (
    <div className="w-full max-w-md space-y-3">
      <p className="text-xs font-medium text-muted-foreground">Skill Summary</p>
      <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
        <div className="flex justify-between">
          <span className="text-xs text-muted-foreground">Name</span>
          <span className="text-xs font-medium text-foreground">{config.display_name || config.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-muted-foreground">Kind</span>
          <span className="text-xs font-medium capitalize text-foreground">{config.kind}</span>
        </div>
        {config.description && (
          <div>
            <span className="text-xs text-muted-foreground">Description</span>
            <p className="mt-0.5 text-xs text-foreground">{config.description}</p>
          </div>
        )}
        {config.keywords?.length > 0 && (
          <div>
            <span className="text-xs text-muted-foreground">Keywords</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {config.keywords.map((kw: string, i: number) => (
                <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-foreground">{kw}</span>
              ))}
            </div>
          </div>
        )}
        {config.visibility && (
          <div className="flex justify-between">
            <span className="text-xs text-muted-foreground">Visibility</span>
            <span className="text-xs font-medium capitalize text-foreground">{config.visibility}</span>
          </div>
        )}
        {config.instructions_preview && (
          <div>
            <span className="text-xs text-muted-foreground">Instructions preview</span>
            <p className="mt-0.5 text-xs text-muted-foreground">{config.instructions_preview}</p>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSelect('confirm', 'Create this skill')}
          className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          Create Skill
        </button>
        <button
          type="button"
          onClick={() => onSelect('edit', 'I want to make changes')}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          Edit
        </button>
      </div>
    </div>
  );
};

const SkillCreatedStep: React.FC<{
  result: any;
  onCreateAnother?: () => void;
  onGoBack?: () => void;
}> = ({ result, onCreateAnother, onGoBack }) => (
  <div className="w-full max-w-md space-y-3">
    <div className="flex items-center gap-2">
      <CheckCircle2 className="h-5 w-5 text-green-500" />
      <p className="text-sm font-medium text-foreground">
        {result.success ? result.message || 'Skill created successfully!' : `Failed: ${result.error}`}
      </p>
    </div>
    {result.success && (
      <div className="flex gap-2">
        {onGoBack && (
          <button
            type="button"
            onClick={onGoBack}
            className="rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
          >
            Back to Skills
          </button>
        )}
        {onCreateAnother && (
          <button
            type="button"
            onClick={onCreateAnother}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
          >
            Create Another
          </button>
        )}
      </div>
    )}
  </div>
);

const ValidationResultStep: React.FC<{
  result: any;
  onSelect: (value: string, label: string) => void;
}> = ({ result }) => (
  <div className="w-full max-w-md space-y-2">
    <div className="flex items-center gap-2">
      {result.valid ? (
        <CheckCircle2 className="h-4 w-4 text-green-500" />
      ) : (
        <ClipboardCheck className="h-4 w-4 text-destructive" />
      )}
      <p className="text-xs font-medium text-foreground">
        {result.valid ? 'Configuration is valid' : 'Validation failed'}
      </p>
    </div>
    {result.errors?.length > 0 && (
      <div className="space-y-1">
        {result.errors.map((err: string, i: number) => (
          <p key={i} className="text-xs text-destructive">{err}</p>
        ))}
      </div>
    )}
    {result.warnings?.length > 0 && (
      <div className="space-y-1">
        {result.warnings.map((w: string, i: number) => (
          <p key={i} className="text-xs text-yellow-600">{w}</p>
        ))}
      </div>
    )}
  </div>
);

const STEP_COMPONENTS: Record<string, React.FC<any>> = {
  name_suggestions: NameSuggestionsStep,
  instructions_generated: InstructionsGeneratedStep,
  instructions_refined: InstructionsRefinedStep,
  keyword_suggestions: KeywordSuggestionsStep,
  structured_output_suggestions: StructuredOutputStep,
  validation_result: ValidationResultStep,
  skill_summary: SkillSummaryStep,
  skill_created: SkillCreatedStep,
};

export const SkillChatBuilder: React.FC<SkillChatBuilderProps> = ({
  onBack,
  showBackButton = true,
  title = 'Create Skill with ORA',
  subtitle = 'Follow the steps to build your skill',
  externalPrompt = null,
  onExternalPromptConsumed,
  draftSkillConfig,
  onDraftSkillConfigChange,
}) => {
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [localDraftSkillConfig, setLocalDraftSkillConfig] = useState<SkillBuilderDraftConfig | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const consumedExternalPromptRef = useRef<string | null>(null);
  const lastProcessedToolMessageIdRef = useRef<string | null>(null);
  const authInfo: any = useAuthInfo();
  const userInitials = getChatUserInitials(authInfo?.user);

  const isDraftControlled = draftSkillConfig !== undefined;
  const effectiveDraftSkillConfig = isDraftControlled ? draftSkillConfig : localDraftSkillConfig;

  const commitDraftSkillConfig = useCallback(
    (nextDraft: SkillBuilderDraftConfig | null) => {
      if (!isDraftControlled) {
        setLocalDraftSkillConfig(nextDraft);
      }
      if (onDraftSkillConfigChange) {
        onDraftSkillConfigChange(nextDraft);
      }
    },
    [isDraftControlled, onDraftSkillConfigChange],
  );

  const { messages, isStreaming, isAssistantTyping, sendMessage, resetChat } = useSkillBuilderStream({
    draftSkillConfig: effectiveDraftSkillConfig,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!isDraftControlled) return;
    setLocalDraftSkillConfig(draftSkillConfig ?? null);
  }, [draftSkillConfig, isDraftControlled]);

  // Sync draft from tool results
  useEffect(() => {
    const latestToolMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'tool' && msg.toolCall?.status === 'complete' && msg.toolCall?.result?.ui_step);
    if (!latestToolMessage) return;
    if (lastProcessedToolMessageIdRef.current === latestToolMessage.id) return;

    lastProcessedToolMessageIdRef.current = latestToolMessage.id;
    const result = latestToolMessage.toolCall?.result || {};

    if (result.ui_step === 'name_suggestions' && result.description) {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        description: result.description,
      });
    } else if (result.ui_step === 'instructions_generated') {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        name: result.skill_name,
        displayName: result.skill_name,
        description: result.description || (effectiveDraftSkillConfig || {}).description,
        kind: result.kind || 'capability',
        instructionsContent: result.instructions_markdown,
      });
    } else if (result.ui_step === 'keyword_suggestions') {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        keywords: result.suggested_keywords,
      });
    } else if (result.ui_step === 'instructions_refined' && result.instructions_markdown) {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        instructionsContent: result.instructions_markdown,
      });
    } else if (result.ui_step === 'structured_output_suggestions') {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        structuredOutput: result.suggested_fields,
      });
    } else if (result.ui_step === 'skill_summary' && result.skill_config) {
      commitDraftSkillConfig({
        ...(effectiveDraftSkillConfig || {}),
        ...result.skill_config,
      });
    }
  }, [messages, effectiveDraftSkillConfig, commitDraftSkillConfig]);

  // Handle external prompts
  useEffect(() => {
    if (!externalPrompt) {
      consumedExternalPromptRef.current = null;
    }
  }, [externalPrompt]);

  useEffect(() => {
    if (!externalPrompt || isStreaming) return;
    if (consumedExternalPromptRef.current === externalPrompt) return;

    consumedExternalPromptRef.current = externalPrompt;
    sendMessage(externalPrompt, {
      draftOverride: effectiveDraftSkillConfig ?? null,
    });
    if (onExternalPromptConsumed) onExternalPromptConsumed();
  }, [effectiveDraftSkillConfig, externalPrompt, isStreaming, onExternalPromptConsumed, sendMessage]);

  const handleSend = () => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleStepSelect = useCallback(
    (uiStep: string | undefined, value: string, label: string) => {
      const currentDraft = effectiveDraftSkillConfig || {};
      let nextDraft: SkillBuilderDraftConfig | null = null;

      if (uiStep === 'name_suggestions') {
        nextDraft = { ...currentDraft, name: value, displayName: value };
      } else if (uiStep === 'keyword_suggestions' && value !== 'custom') {
        try {
          nextDraft = { ...currentDraft, keywords: JSON.parse(value) };
        } catch { /* ignore */ }
      } else if (uiStep === 'structured_output_suggestions' && value !== 'skip') {
        try {
          nextDraft = { ...currentDraft, structuredOutput: JSON.parse(value) };
        } catch { /* ignore */ }
      }

      if (uiStep === 'instructions_generated' && value === 'use_instructions') {
        // Ensure current draft (already synced from tool result) is sent as override
        nextDraft = { ...currentDraft };
      }

      if (nextDraft) {
        commitDraftSkillConfig(nextDraft);
      }

      let userMessage: string;
      if (uiStep === 'name_suggestions') {
        userMessage = `I'll name it "${value}"`;
      } else if (uiStep === 'instructions_generated') {
        userMessage = label;
      } else if (uiStep === 'keyword_suggestions') {
        if (value === 'custom') {
          userMessage = 'I want to set my own keywords';
        } else {
          try {
            const parsed: string[] = JSON.parse(value);
            if (!parsed.length) {
              userMessage = 'No keywords needed, skip this step';
            } else {
              userMessage = `I want to use these keywords: ${parsed.join(', ')}`;
            }
          } catch {
            userMessage = 'Use the selected keywords';
          }
        }
      } else if (uiStep === 'structured_output_suggestions') {
        if (value === 'skip') {
          userMessage = 'No structured output needed, skip this step';
        } else {
          try {
            const parsed = JSON.parse(value);
            const names = parsed.map((f: any) => f.fieldName);
            userMessage = `I want to use these output fields: ${names.join(', ')}`;
          } catch {
            userMessage = 'Use the selected output fields';
          }
        }
      } else if (uiStep === 'skill_summary') {
        userMessage = value === 'confirm' ? 'Yes, create this skill!' : 'I want to make some changes first';
      } else {
        userMessage = label;
      }

      sendMessage(userMessage, nextDraft ? { draftOverride: nextDraft } : undefined);
    },
    [effectiveDraftSkillConfig, commitDraftSkillConfig, sendMessage],
  );

  return (
    <div className="flex h-full min-h-0 bg-background">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border bg-background px-6 py-4">
          <div className="flex items-center gap-4">
            {showBackButton ? (
              <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : null}
            <div className="flex items-center gap-3">
              <div className="rounded-lg border border-border bg-muted p-2">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold text-foreground">{title}</h1>
                <p className="text-[13px] text-muted-foreground">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl space-y-6">
            {messages.map((message) => {
              if (message.role === 'tool' && message.toolCall) {
                const isRunning = message.toolCall.status === 'running';
                const uiStep = message.toolCall.result?.ui_step;
                const StepComponent = STEP_COMPONENTS[uiStep];

                if (StepComponent) {
                  const extraProps: Record<string, unknown> = {};
                  if (uiStep === 'skill_created') {
                    extraProps.onCreateAnother = () => {
                      commitDraftSkillConfig(null);
                      resetChat();
                    };
                    extraProps.onGoBack = onBack;
                  }

                  return (
                    <div key={message.id} className="flex justify-start gap-3">
                      <ChatMessageAvatar role="assistant" />
                      <div className="rounded-lg border border-border bg-card px-4 py-3">
                        <div className="mb-2 flex items-center gap-1.5">
                          {STEP_ICONS[uiStep] || <Wrench className="h-4 w-4" />}
                          <span className="text-[11px] font-medium text-muted-foreground">
                            {uiStep?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </span>
                        </div>
                        <StepComponent
                          result={message.toolCall.result}
                          onSelect={(value: string, label: string) => {
                            handleStepSelect(uiStep, value, label);
                          }}
                          {...extraProps}
                        />
                      </div>
                    </div>
                  );
                }

                if (!isRunning) return null;

                return (
                  <div key={message.id} className="flex justify-start gap-3">
                    <ChatMessageAvatar role="assistant" />
                    <div className="rounded-lg border border-border bg-card px-4 py-2">
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {TOOL_LOADING_LABELS[message.toolCall.tool_name] ?? 'Processing...'}
                      </span>
                    </div>
                  </div>
                );
              }

              if (message.role === 'assistant' || message.role === 'user') {
                return (
                  <div
                    key={message.id}
                    className={cn(
                      'flex gap-3',
                      message.role === 'user' ? 'justify-end' : 'justify-start',
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <ChatMessageAvatar role="assistant" className="mt-0.5" />
                    ) : null}
                    <div className={cn('flex max-w-[75%] flex-col gap-1', message.role === 'user' ? 'items-end' : 'items-start')}>
                      <ChatSenderLabel role={message.role} />
                      <div
                        className={cn(
                          'w-full rounded-lg px-3.5 py-2.5',
                          message.role === 'user'
                            ? 'rounded-tr-sm bg-accent-tint text-foreground'
                            : 'rounded-tl-sm border border-border bg-quaternary text-foreground',
                        )}
                      >
                        {message.isLoading ? (
                          <div className="flex gap-1 py-1">
                            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '0ms' }} />
                            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '150ms' }} />
                            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '300ms' }} />
                          </div>
                        ) : message.role === 'user' ? (
                          <p className="whitespace-pre-wrap text-sm leading-[1.8] text-foreground">{message.content}</p>
                        ) : message.content ? (
                          <MarkdownRenderer classname="text-sm [&_p]:text-sm [&_p]:leading-[1.8] [&_p]:text-foreground [&_li]:text-sm [&_li]:leading-[1.8] [&_li]:text-muted-foreground [&_ul]:text-sm [&_ol]:text-sm [&_strong]:text-foreground">
                            {message.content}
                          </MarkdownRenderer>
                        ) : null}
                      </div>
                    </div>
                    {message.role === 'user' ? (
                      <ChatMessageAvatar role="user" userInitials={userInitials} className="mt-0.5" />
                    ) : null}
                  </div>
                );
              }

              return null;
            })}

            {isStreaming && !isAssistantTyping && !(messages[messages.length - 1]?.role === 'tool' && (messages[messages.length - 1] as any)?.toolCall?.status === 'running') ? (
              <div className="flex gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-muted">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
                </div>
                <div className="rounded-lg rounded-tl-sm border border-border bg-quaternary px-3.5 py-2.5">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t border-border bg-background p-4">
          <div className="mx-auto max-w-4xl">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSend();
              }}
            >
              <div
                className={cn(
                  'relative rounded-lg border bg-background transition-all duration-200',
                  isFocused ? 'border-foreground/30' : 'border-border',
                )}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe your skill or respond to ORA..."
                  disabled={isStreaming}
                  className="w-full bg-transparent px-5 py-4 pr-14 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isStreaming}
                  className={cn(
                    'absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[5px] transition-all',
                    input.trim() && !isStreaming
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'cursor-not-allowed bg-muted text-muted-foreground',
                  )}
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-2.5 flex items-center px-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      isStreaming ? 'animate-pulse bg-muted-foreground' : 'bg-foreground',
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isStreaming ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {isStreaming ? 'Thinking...' : 'AI Ready'}
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillChatBuilder;
